import React, { useState, useRef } from 'react';
import { useWalletKit } from '@mysten/wallet-kit';
import './EnhancedUserSignup.css';

interface EnhancedUserSignupProps {
  onSignupSuccess: (userData: any, sessionToken: string) => void;
  onLoginClick: () => void;
  onClose: () => void;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '/api';

const EnhancedUserSignup: React.FC<EnhancedUserSignupProps> = ({
  onSignupSuccess,
  onLoginClick,
  onClose
}) => {
  const { currentAccount, isConnected } = useWalletKit();
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile) return null;

    const formData = new FormData();
    formData.append('file', avatarFile);

    try {
      const response = await fetch(`${BACKEND_URL}/uploads/avatar`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        return result.url;
      }
    } catch (err) {
      console.error('Avatar upload failed:', err);
    }
    return null;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isConnected || !currentAccount) {
      setError('Please connect your wallet first');
      return;
    }

    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Upload avatar if selected
      let avatarUrl = null;
      if (avatarFile) {
        avatarUrl = await uploadAvatar();
      }

      // Create user account
      const response = await fetch(`${BACKEND_URL}/auth/user/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress: currentAccount.address,
          profileData: {
            username: username.trim(),
            bio: bio.trim(),
            avatar: avatarUrl || avatarPreview || ''
          }
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        localStorage.setItem('userSessionToken', result.sessionToken);
        localStorage.setItem('userData', JSON.stringify(result.user));
        
        setTimeout(() => {
          onSignupSuccess(result.user, result.sessionToken);
        }, 1500);
      } else {
        setError(result.error || 'Signup failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateAvatar = () => {
    if (currentAccount) {
      const colors = ['#667eea', '#764ba2', '#f59e0b', '#10b981', '#ef4444'];
      const color = colors[Math.floor(Math.random() * colors.length)];
      const initial = username ? username[0].toUpperCase() : 'U';
      
      const svg = `
        <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
          <rect width="200" height="200" fill="${color}"/>
          <text x="100" y="100" font-family="Arial" font-size="80" fill="white" text-anchor="middle" dy=".35em">${initial}</text>
        </svg>
      `;
      
      const dataUrl = `data:image/svg+xml;base64,${btoa(svg)}`;
      setAvatarPreview(dataUrl);
    }
  };

  return (
    <div className="enhanced-signup-overlay">
      <div className="enhanced-signup-container">
        {success && (
          <div className="success-banner">
            Account Created Successfully!
            <button className="close-success" onClick={() => setSuccess(false)}>×</button>
          </div>
        )}
        
        <div className="signup-header">
          <h1>Create Account</h1>
          <p>Set up your profile to get started</p>
        </div>

        <form className="signup-form" onSubmit={handleSignup}>
          <div className="avatar-section">
            <div className="avatar-preview" onClick={() => fileInputRef.current?.click()}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" />
              ) : (
                <div className="avatar-placeholder">
                  <span>📷</span>
                  <p>Upload Photo</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="generate-avatar-btn"
              onClick={generateAvatar}
            >
              Generate Avatar
            </button>
          </div>

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="bio">Bio (Optional)</label>
            <textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell us about yourself..."
              className="form-textarea"
              rows={3}
            />
          </div>

          {isConnected && currentAccount && (
            <div className="wallet-display">
              <span className="wallet-label">Wallet:</span>
              <span className="wallet-address">
                {currentAccount.address.slice(0, 6)}...{currentAccount.address.slice(-4)}
              </span>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="signup-button"
            disabled={loading || !isConnected}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="divider">
            <span>OR</span>
          </div>

          <div className="login-link">
            <span>Already have an account? </span>
            <button type="button" onClick={onLoginClick} className="link-button">
              Sign in here
            </button>
          </div>

          <button type="button" className="close-button" onClick={onClose}>
            Back to App
          </button>
        </form>
      </div>
    </div>
  );
};

export default EnhancedUserSignup;
